<template>
  <div class="flex flex-col bg-gray-50 h-full min-h-0 flex-1">
    <PageHeader
      v-if="!embedded"
      title="Agent"
      subtitle="Explore the warehouse and author artifacts with a live agent — beta"
    />
    <div class="flex-1 min-h-0 flex flex-col px-4 pb-4" :class="embedded ? 'pt-1' : 'pt-3'">
    <!-- state banner -->
    <div class="flex items-center gap-2 pb-2">
      <span class="w-2 h-2 rounded-full" :class="dotClass" />
      <span class="text-xs text-gray-500">{{ stateLabel }}</span>
      <span v-if="s.totalCost" class="text-xs text-gray-400 tabular-nums">
        session cost ${{ s.totalCost.toFixed(2) }}
      </span>
      <div class="ml-auto flex items-center gap-2">
        <button
          v-if="!sessionActive"
          type="button"
          class="text-xs px-2 py-0.5 border rounded text-gray-600 hover:bg-gray-50"
          @click="toggleHistory"
        >
          History
        </button>
        <button
          v-if="!sessionActive"
          type="button"
          class="text-xs px-2 py-0.5 border rounded text-gray-600 hover:bg-gray-50"
          @click="startLive"
        >
          {{ s.connState === 'idle' ? (startLabel || 'Start session') : 'Reconnect' }}
        </button>
        <!-- Always available while a session is up OR still launching: a
             worker that never pairs would otherwise leave no usable control. -->
        <button
          v-if="sessionActive"
          type="button"
          class="text-xs px-2 py-0.5 border rounded text-gray-400 hover:text-red-600 hover:border-red-300"
          @click="endSession"
        >
          {{ s.connState === 'launching' || s.connState === 'connecting' ? 'Cancel' : 'End session' }}
        </button>
      </div>
    </div>

    <div v-if="contextMismatch" class="mb-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
      This active session was started without this metric’s Wizard context. End it, then start the Wizard session.
    </div>

    <!-- session history -->
    <div v-if="showHistory" class="border rounded-lg bg-white mb-2 max-h-56 overflow-y-auto">
      <p v-if="!pastSessions.length" class="p-3 text-xs text-gray-400">No past sessions.</p>
      <!-- Clicking a past session CONTINUES it: the conversation loads and a
           live agent picks up where it stopped. -->
      <button
        v-for="ps in pastSessions"
        :key="ps.session_id"
        type="button"
        class="w-full text-left px-3 py-2 border-b last:border-b-0 hover:bg-gray-50 flex items-baseline gap-3"
        title="Continue this conversation with a live agent"
        @click="resumeSession(ps.session_id)"
      >
        <span class="text-sm text-gray-800 truncate flex-1">{{ ps.title || '(no message)' }}</span>
        <span class="text-xs text-gray-400 whitespace-nowrap">{{ new Date(ps.created_at).toLocaleString() }}</span>
        <span class="text-xs text-gray-400 tabular-nums">${{ (ps.total_cost_usd || 0).toFixed(2) }}</span>
        <span class="text-[11px]" :class="ps.status === 'died' ? 'text-red-400' : 'text-gray-300'">{{ ps.status }}</span>
      </button>
    </div>

    <div ref="panes" class="flex-1 min-h-0 flex">
      <!-- ─────────── LEFT: conversation ─────────── -->
      <div class="flex-1 min-w-0 flex flex-col">
        <div ref="scroll" class="flex-1 min-h-0 overflow-y-auto border rounded-lg bg-white">
          <div v-if="!s.blocks.length" class="p-8 text-sm text-gray-400 max-w-xl">
            <template v-if="sessionMode === 'analysis'">
              <p class="font-medium text-gray-500 mb-2">Design an R analysis with a live agent.</p>
              <p>
                It can inspect saved Datasets, direct DataAttributes, profile results,
                existing studies and vetted R designs, then work with you to author an
                executable study without hardcoding a clinical pilot.
              </p>
            </template>
            <template v-else-if="sessionMode === 'ingestion'">
              <p class="font-medium text-gray-500 mb-2">Manage this tenant's ingestion with a live agent.</p>
              <p>
                It boots holding the whole estate — routines, schemas, feeds and
                recent failures — and can trace a missing file, work out a messy
                file's real structure, and propose a parser schema. Proposals
                land as drafts for you to confirm.
              </p>
            </template>
            <template v-else>
              <p class="font-medium text-gray-500 mb-2">Explore the warehouse with a live agent.</p>
              <p>
                It queries Snowflake and Postgres as you talk, tests assumptions,
                and draws what it finds on the scratchpad to the right. When
                you're happy, tell it to save — metrics, flags, worklists and
                insights land in the studio directly.
              </p>
            </template>
            <p class="mt-2 text-xs">
              The agent loads its context when you start a session and
              introduces itself when ready (usually a minute or two). The session
              survives navigating the app; it ends if you close or reload the page.
            </p>
          </div>

          <div v-for="(b, i) in s.blocks" :key="i" class="px-4 py-1.5">
            <div v-if="b.kind === 'user'" class="max-w-3xl rounded-md border border-primary-200 bg-primary-50 px-3 py-2">
              <div class="flex items-center gap-2 text-[11px] font-medium text-primary-700 mb-1">
                <span class="w-1.5 h-1.5 rounded-full bg-primary-500" />
                You
              </div>
              <p class="text-xs leading-relaxed text-gray-700 whitespace-pre-wrap">{{ b.text }}</p>
            </div>

            <div v-else-if="b.kind === 'text'" class="max-w-3xl rounded-md border border-sky-200 bg-sky-50 px-3 py-2">
              <div class="flex items-center gap-2 text-[11px] font-medium text-sky-700 mb-1">
                <span class="w-1.5 h-1.5 rounded-full bg-sky-500" />
                Agent
              </div>
              <p class="text-xs leading-relaxed text-gray-700 whitespace-pre-wrap">{{ b.text }}</p>
            </div>

            <div v-else-if="b.kind === 'question'" class="max-w-3xl rounded-md border border-violet-200 bg-violet-50 px-3 py-2">
              <div class="flex items-center gap-2 text-[11px] font-medium text-violet-700 mb-1">
                <span class="w-1.5 h-1.5 rounded-full bg-violet-500" />
                Decision needed
              </div>
              <p class="text-xs leading-relaxed text-gray-700 mb-2">{{ b.question }}</p>
              <div class="grid gap-1.5">
                <button
                  v-for="option in b.options"
                  :key="option.label"
                  type="button"
                  class="text-left rounded border px-2.5 py-2 text-xs transition-colors"
                  :class="b.answered === option.label
                    ? 'border-violet-500 bg-violet-100 text-violet-900'
                    : 'border-violet-200 bg-white text-gray-700 hover:border-violet-400'"
                  :disabled="!!b.answered"
                  @click="answerQuestion(b, option.label)"
                >
                  <span class="font-medium">{{ option.label }}</span>
                  <span class="block text-gray-500 mt-0.5">{{ option.description }}</span>
                </button>
              </div>
              <div v-if="!b.answered" class="flex gap-1.5 mt-2">
                <input
                  v-model="b.responseText"
                  class="flex-1 rounded border border-violet-200 bg-white px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-violet-300"
                  placeholder="Or write your answer"
                  @keydown.enter.prevent="answerQuestion(b)"
                />
                <button
                  type="button"
                  class="rounded border border-violet-300 px-2.5 text-xs text-violet-700 hover:bg-violet-100 disabled:opacity-40"
                  :disabled="!b.responseText || !b.responseText.trim()"
                  @click="answerQuestion(b)"
                >Answer</button>
              </div>
              <p v-else class="mt-2 text-[11px] text-violet-700">Answer: {{ b.answered }}</p>
            </div>

            <button
              v-else-if="b.kind === 'thinking'"
              type="button"
              class="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-left"
              :class="[
                b.collapsed ? 'w-full' : 'max-w-3xl',
                b.done ? 'cursor-pointer hover:border-gray-300' : 'cursor-default',
              ]"
              :disabled="!b.done"
              @click="b.collapsed = !b.collapsed"
            >
              <div class="flex items-center gap-2 text-[11px] font-medium text-gray-500" :class="b.collapsed ? '' : 'mb-1'">
                <span class="w-1.5 h-1.5 rounded-full" :class="b.done ? 'bg-gray-300' : 'bg-green-500 animate-pulse'" />
                Thinking
                <span v-if="b.done" class="ml-auto text-gray-400 font-normal">
                  {{ b.collapsed ? 'Click to expand' : 'Click to collapse' }}
                </span>
              </div>
              <p
                v-if="!b.collapsed"
                class="text-xs leading-relaxed text-gray-600 whitespace-pre-wrap"
              >{{ b.text }}</p>
            </button>

            <button
              v-else-if="b.kind === 'visual_ref'"
              type="button"
              class="flex items-center gap-2 text-xs text-sky-700 hover:underline"
              @click="scrollToVisual(b.visualId)"
            >
              <span>📊</span>
              <span>{{ b.title }} → scratchpad</span>
            </button>

            <div
              v-else-if="b.kind === 'created'"
              class="my-1 max-w-xl border rounded-lg px-3 py-2"
              :class="b.ok ? 'border-green-300 bg-green-50' : 'border-red-200 bg-red-50'"
            >
              <p v-if="b.ok" class="text-sm text-green-800">
                <span class="font-semibold">{{ b.artifactLabel }} {{ b.action }}</span> — #{{ b.id }} {{ b.name }}
              </p>
              <p v-else class="text-sm text-red-700">{{ b.artifactLabel }} create failed: {{ b.error }}</p>
            </div>

            <div
              v-else-if="b.kind === 'upload'"
              class="my-1 max-w-xl border rounded-lg px-3 py-2"
              :class="b.ok ? 'border-green-300 bg-green-50' : 'border-red-200 bg-red-50'"
            >
              <p v-if="b.ok" class="text-xs text-green-800">
                <span class="font-semibold">Uploaded</span> {{ b.name }}
                <span class="block font-mono text-[10px] text-green-700 mt-0.5 break-all">{{ b.key }}</span>
              </p>
              <p v-else class="text-xs text-red-700">
                <span class="font-semibold">Upload failed</span> — {{ b.name }}: {{ b.error }}
              </p>
            </div>

            <p v-else-if="b.kind === 'error'" class="text-xs text-red-600">{{ b.text }}</p>
            <p v-else-if="b.kind === 'meta'" class="text-[11px] text-gray-300 tabular-nums">{{ b.text }}</p>
          </div>

          <div v-if="thinking" class="px-4 py-2 text-xs text-gray-400">
            <span class="animate-pulse">working… {{ thinkingElapsed }}</span>
          </div>
        </div>

        <!-- composer -->
        <div class="flex items-end gap-2 pt-2">
          <textarea
            v-model="draft"
            rows="2"
            class="flex-1 border rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-orange-300"
            :placeholder="composerPlaceholder"
            :disabled="hasPendingQuestion || (s.connState !== 'ready' && s.connState !== 'thinking')"
            @keydown.enter.exact.prevent="sendDraft"
          />
          <button
            v-if="thinking"
            type="button"
            class="px-3 py-2 text-sm border rounded-lg text-gray-600 hover:bg-gray-50"
            @click="interrupt"
          >
            Stop
          </button>
          <button
            type="button"
            class="px-4 py-2 text-sm rounded-lg bg-orange-600 text-white disabled:opacity-40"
            :disabled="!draft.trim() || hasPendingQuestion || (s.connState !== 'ready' && s.connState !== 'thinking')"
            @click="sendDraft"
          >
            Send
          </button>
        </div>
      </div>

      <!-- divider: drag to resize, double-click to reset -->
      <div
        v-if="panels.length"
        class="ae-divider"
        title="Drag to resize · double-click to reset"
        @mousedown.prevent="startDivider"
        @dblclick="resetDivider"
      />

      <!-- ─────────── RIGHT: the reference widgets this mount opted into ─────────── -->
      <div
        v-if="panels.length"
        class="min-w-0 flex flex-col"
        :style="{ width: (sessionMode === 'analysis' ? 75 : padPct) + '%', flexShrink: 0 }"
      >
        <!-- R code is the primary analysis-agent artifact -->
        <div v-if="primaryPanel === 'r-code'" class="flex flex-1 min-h-0 gap-2">
          <div class="flex-1 min-w-0 min-h-0 flex flex-col border rounded-lg bg-white">
            <div class="flex items-center gap-2 px-3 py-2 border-b">
              <button
                type="button"
                class="text-xs font-semibold uppercase tracking-wide"
                :class="rPane === 'code' ? 'text-gray-700' : 'text-gray-400'"
                @click="rPane = 'code'"
              >R Code</button>
              <button
                type="button"
                class="text-xs font-semibold uppercase tracking-wide"
                :class="rPane === 'output' ? 'text-gray-700' : 'text-gray-400'"
                @click="rPane = 'output'"
              >R Output</button>
              <span v-if="rPane === 'code' && s.rCode" class="text-[10px] text-gray-400 font-mono">{{ s.rCode.name }}</span>
              <span v-if="rPane === 'code' && s.rCode" class="ml-auto text-[10px] text-gray-400">{{ s.rCode.status }}</span>
              <span v-if="rPane === 'output' && s.rOutput" class="ml-auto text-[10px] text-gray-400">{{ s.rOutput.status }}</span>
            </div>
            <div v-if="rPane === 'code'" class="flex-1 min-h-0 overflow-auto bg-gray-900">
              <pre v-if="s.rCode" class="p-3 text-[11px] leading-relaxed text-gray-100 font-mono whitespace-pre-wrap break-words">{{ s.rCode.script }}</pre>
              <p v-else class="text-xs text-gray-400 p-4">The analysis agent's current R script appears here as it designs the study.</p>
            </div>
            <div v-else class="flex-1 min-h-0 overflow-hidden">
              <p v-if="!s.rOutput" class="text-xs text-gray-400 p-4">Run an R analysis to see its outputs here.</p>
              <div v-else-if="['pending', 'running'].includes(s.rOutput.status)" class="h-full overflow-auto p-3">
                <p class="text-xs text-gray-600">{{ s.rOutput.status === 'pending' ? 'Queued' : 'Running' }}…</p>
                <pre v-if="s.rOutput.console" class="sensitive mt-2 text-[10px] text-gray-500 whitespace-pre-wrap" data-demo-blur>{{ s.rOutput.console }}</pre>
              </div>
              <div v-else-if="s.rOutput.status === 'failed'" class="p-3 text-xs text-red-600">{{ s.rOutput.error || 'R analysis failed.' }}</div>
              <p v-else-if="!rOutputs.length" class="text-xs text-gray-400 p-4">The run completed without outputs.</p>
              <div v-else class="flex h-full min-h-0">
                <div class="w-40 shrink-0 overflow-y-auto border-r border-gray-200 py-1">
                  <button
                    v-for="(output, index) in rOutputs"
                    :key="`${output.kind}-${output.name}-${index}`"
                    type="button"
                    class="w-full px-2 py-1.5 text-left"
                    :class="rOutputIndex === index ? 'bg-gray-100' : 'hover:bg-gray-25'"
                    @click="rOutputIndex = index"
                  >
                    <span class="block text-[10px] uppercase text-gray-400">{{ output.kind }}</span>
                    <span class="block text-xs text-gray-700 break-words">{{ output.name }}</span>
                  </button>
                </div>
                <div v-if="currentROutput" class="flex-1 min-w-0 min-h-0 overflow-auto p-3">
                  <template v-if="currentROutput.kind === 'effect'">
                    <div class="text-xs font-semibold text-gray-700 mb-2">{{ currentROutput.name }}</div>
                    <pre class="text-xs font-mono text-gray-700 whitespace-pre-wrap">estimate  {{ currentROutput.estimate }}
ci_low    {{ currentROutput.ci_low }}
ci_high   {{ currentROutput.ci_high }}
p         {{ currentROutput.p }}
unit      {{ currentROutput.unit }}
model     {{ currentROutput.model }}</pre>
                  </template>
                  <div v-else-if="currentROutput.kind === 'table'" data-private class="h-full min-h-48">
                    <AgGridVue
                      :theme="listTheme"
                      style="width: 100%; height: 100%"
                      :column-defs="rOutputColumnDefs"
                      :default-col-def="rOutputDefaultColDef"
                      :row-data="currentROutput.rows || []"
                    />
                  </div>
                  <template v-else-if="currentROutput.kind === 'plot'">
                    <div class="text-xs font-semibold text-gray-700 mb-2">{{ currentROutput.name }}</div>
                    <img v-if="rOutputPlotUrls[rOutputPlotKey(currentROutput)]" :src="rOutputPlotUrls[rOutputPlotKey(currentROutput)]" :alt="currentROutput.name" class="max-w-full" />
                    <p v-else class="text-xs text-gray-400">Loading plot…</p>
                  </template>
                  <template v-else>
                    <div class="text-xs font-semibold text-gray-700 mb-2">{{ currentROutput.name }}</div>
                    <p class="text-xs text-gray-600 whitespace-pre-wrap">{{ currentROutput.detail }}</p>
                  </template>
                  <p v-if="currentROutput.caption" class="text-[10px] text-gray-400 mt-2">{{ currentROutput.caption }}</p>
                </div>
              </div>
            </div>
          </div>
          <div v-if="hasPanel('selected-data')" class="w-72 shrink-0 min-h-0 flex flex-col border rounded-lg bg-white">
            <div class="flex items-center justify-between gap-2 px-3 py-2 border-b">
              <span class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Selected data</span>
              <button type="button" class="text-xs text-primary-600 hover:text-primary-700" @click="$emit('select-data')">Select Data</button>
            </div>
            <div class="flex-1 min-h-0 overflow-y-auto px-3 py-2">
              <p v-if="!selectedInputs.length" class="text-xs text-gray-400">No data selected.</p>
              <template v-else>
                <div v-if="selectedDatasets.length">
                  <div class="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Datasets</div>
                  <div class="space-y-1">
                    <div v-for="item in selectedDatasets" :key="`selected-dataset-${item.id}`" class="sensitive text-xs text-gray-600 break-words">{{ item.name }}</div>
                  </div>
                </div>
                <div v-if="selectedAttributes.length" :class="selectedDatasets.length ? 'mt-4' : ''">
                  <div class="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">DataAttributes</div>
                  <div class="space-y-1">
                    <div v-for="item in selectedAttributes" :key="`selected-attribute-${item.id}`" class="sensitive text-xs text-gray-600 break-words">{{ item.name }}</div>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>

        <!-- scratchpad (paged) -->
        <div v-else-if="primaryPanel === 'scratchpad'" class="flex-1 min-h-0 flex flex-col border rounded-lg bg-gray-50/70">
          <div class="flex items-center gap-2 px-3 py-2 border-b bg-white rounded-t-lg">
            <span class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Scratchpad</span>
            <template v-if="s.visuals.length">
              <button
                type="button"
                class="text-xs text-gray-400 hover:text-gray-600 disabled:opacity-30"
                :disabled="padPage <= 0"
                @click="padPage--"
              >
                <font-awesome-icon :icon="['fas', 'chevron-left']" />
              </button>
              <span class="text-xs text-gray-500 tabular-nums">{{ padPage + 1 }} / {{ s.visuals.length }}</span>
              <button
                type="button"
                class="text-xs text-gray-400 hover:text-gray-600 disabled:opacity-30"
                :disabled="padPage >= s.visuals.length - 1"
                @click="padPage++"
              >
                <font-awesome-icon :icon="['fas', 'chevron-right']" />
              </button>
            </template>
            <button
              v-if="s.visuals.length"
              type="button"
              class="ml-auto text-xs text-gray-400 hover:text-gray-600"
              @click="s.visuals = []; padPage = 0"
            >
              Clear
            </button>
          </div>
          <div class="flex-1 min-h-0 overflow-hidden p-3 flex flex-col">
            <p v-if="!s.visuals.length" class="text-xs text-gray-400 p-4">
              Visuals the agent draws land here.
            </p>

            <template v-else>
              <div
                :key="currentVisual.id"
                class="bg-white border rounded-lg p-3 flex-1 min-h-0 overflow-y-auto"
              >
                <div class="flex items-baseline gap-2 pb-2">
                  <span class="text-sm font-semibold text-gray-700">{{ currentVisual.title }}</span>
                  <span class="text-[11px] text-gray-400">{{ currentVisual.viz_type }}</span>
                </div>

                <iframe
                  v-if="currentVisual.viz_type === 'drawing'"
                  :srcdoc="drawingDoc(currentVisual)"
                  sandbox=""
                  class="w-full border-0"
                  :style="{ aspectRatio: drawingAspect(currentVisual) }"
                  :title="currentVisual.title"
                />

                <div v-else-if="!currentVisual.viz" class="text-xs text-red-600">Could not render this visual.</div>

                <Chart_V6
                  v-else-if="currentVisual.viz.component === 'chart_v6'"
                  :spec="currentVisual.viz.props.spec"
                />

                <div v-else-if="currentVisual.viz.component === 'chart_v5'" class="h-[320px]">
                  <Chart_V5
                    :chart_data="currentVisual.viz.props.chart_data"
                    :chart_config="currentVisual.viz.props.chart_config"
                    override-height="h-full"
                  />
                </div>

                <div v-else-if="currentVisual.viz.component === 'table'" data-private class="h-[320px]">
                  <AgGridVue
                    :theme="listTheme"
                    style="width: 100%; height: 100%"
                    :column-defs="currentVisual.viz.props.columnDefs"
                    :default-col-def="defaultColDef"
                    :row-data="currentVisual.viz.props.rowData"
                  />
                </div>

                <Pie_Chart
                  v-else-if="currentVisual.viz.component === 'pie'"
                  class="h-[320px]"
                  override-height="h-full"
                  :chart_data="currentVisual.viz.props.chart_data"
                  :chart_config="currentVisual.viz.props.chart_config"
                />

                <Sankey_Diagram
                  v-else-if="currentVisual.viz.component === 'sankey'"
                  :chart-data="currentVisual.viz.props.chartData"
                  :config="currentVisual.viz.props.config"
                />

                <HexMap
                  v-else-if="currentVisual.viz.component === 'heatmap'"
                  :chart-data="currentVisual.viz.props.chartData"
                  :chart-config="currentVisual.viz.props.chartConfig"
                />
              </div>
            </template>
          </div>
        </div>

        <!-- BOTTOM: the remaining opted-in widgets side by side -->
        <div
          v-if="bottomPanels.length"
          class="flex gap-2 min-h-0"
          :class="primaryPanel ? 'pt-2' : ''"
          :style="primaryPanel ? 'height: 50%' : 'flex: 1'"
        >
          <!-- Scratchpad in the Study Wizard's secondary slot -->
          <div v-if="hasPanel('scratchpad') && primaryPanel !== 'scratchpad'" class="flex-1 min-w-0 flex flex-col border rounded-lg bg-gray-50/70">
            <div class="flex items-center gap-2 px-3 py-2 border-b bg-white rounded-t-lg">
              <span class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Scratchpad</span>
              <template v-if="s.visuals.length">
                <button type="button" class="text-xs text-gray-400 hover:text-gray-600 disabled:opacity-30" :disabled="padPage <= 0" @click="padPage--">
                  <font-awesome-icon :icon="['fas', 'chevron-left']" />
                </button>
                <span class="text-xs text-gray-500 tabular-nums">{{ padPage + 1 }} / {{ s.visuals.length }}</span>
                <button type="button" class="text-xs text-gray-400 hover:text-gray-600 disabled:opacity-30" :disabled="padPage >= s.visuals.length - 1" @click="padPage++">
                  <font-awesome-icon :icon="['fas', 'chevron-right']" />
                </button>
              </template>
              <button v-if="s.visuals.length" type="button" class="ml-auto text-xs text-gray-400 hover:text-gray-600" @click="s.visuals = []; padPage = 0">Clear</button>
            </div>
            <div class="flex-1 min-h-0 overflow-hidden p-3 flex flex-col">
              <p v-if="!s.visuals.length" class="text-xs text-gray-400 p-4">Visuals the agent draws land here.</p>
              <div v-else :key="currentVisual.id" class="bg-white border rounded-lg p-3 flex-1 min-h-0 overflow-y-auto">
                <div class="flex items-baseline gap-2 pb-2">
                  <span class="text-sm font-semibold text-gray-700">{{ currentVisual.title }}</span>
                  <span class="text-[11px] text-gray-400">{{ currentVisual.viz_type }}</span>
                </div>
                <iframe v-if="currentVisual.viz_type === 'drawing'" :srcdoc="drawingDoc(currentVisual)" sandbox="" class="w-full border-0" :style="{ aspectRatio: drawingAspect(currentVisual) }" :title="currentVisual.title" />
                <div v-else-if="!currentVisual.viz" class="text-xs text-red-600">Could not render this visual.</div>
                <Chart_V6 v-else-if="currentVisual.viz.component === 'chart_v6'" :spec="currentVisual.viz.props.spec" />
                <div v-else-if="currentVisual.viz.component === 'chart_v5'" class="h-[320px]"><Chart_V5 :chart_data="currentVisual.viz.props.chart_data" :chart_config="currentVisual.viz.props.chart_config" override-height="h-full" /></div>
                <div v-else-if="currentVisual.viz.component === 'table'" data-private class="h-[320px]"><AgGridVue :theme="listTheme" style="width: 100%; height: 100%" :column-defs="currentVisual.viz.props.columnDefs" :default-col-def="defaultColDef" :row-data="currentVisual.viz.props.rowData" /></div>
                <Pie_Chart v-else-if="currentVisual.viz.component === 'pie'" class="h-[320px]" override-height="h-full" :chart_data="currentVisual.viz.props.chart_data" :chart_config="currentVisual.viz.props.chart_config" />
                <Sankey_Diagram v-else-if="currentVisual.viz.component === 'sankey'" :chart-data="currentVisual.viz.props.chartData" :config="currentVisual.viz.props.config" />
                <HexMap v-else-if="currentVisual.viz.component === 'heatmap'" :chart-data="currentVisual.viz.props.chartData" :chart-config="currentVisual.viz.props.chartConfig" />
              </div>
            </div>
          </div>

          <!-- Available Tables -->
          <div v-if="hasPanel('tables')" class="flex-1 min-w-0 flex flex-col border rounded-lg bg-white">
            <div class="flex items-center gap-2 px-3 py-2 border-b">
              <span class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Available Tables</span>
              <span v-if="erdSchemas.length" class="text-xs text-gray-400">{{ erdTableCount }}</span>
            </div>
            <div class="flex-1 min-h-0 overflow-y-auto px-3 py-2">
              <p v-if="!erdSchemas.length" class="text-xs text-gray-400">Loading catalog…</p>
              <div v-for="schema in erdSchemas" :key="schema.name" class="mb-2">
                <button
                  type="button"
                  class="w-full text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide hover:text-gray-700 flex items-center gap-1"
                  @click="schema._open = !schema._open"
                >
                  <font-awesome-icon
                    :icon="['fas', schema._open ? 'chevron-down' : 'chevron-right']"
                    class="text-[8px] text-gray-400"
                  />
                  {{ schema.name }}
                  <span class="font-normal text-gray-400 ml-1">{{ Object.keys(schema.tables).length }}</span>
                </button>
                <div v-if="schema._open" class="pl-3 mt-1 space-y-0.5">
                  <div
                    v-for="(table, tname) in schema.tables"
                    :key="tname"
                    class="text-[11px] text-gray-600 truncate"
                    :title="table.description || tname"
                  >
                    {{ tname }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Data Sources (agent-declared) -->
          <div v-if="hasPanel('sources')" class="flex-1 min-w-0 flex flex-col border rounded-lg bg-white">
            <div class="flex items-center gap-2 px-3 py-2 border-b">
              <span class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Data Sources</span>
              <span v-if="s.sources.length" class="text-xs text-gray-400">{{ s.sources.length }}</span>
            </div>
            <div class="flex-1 min-h-0 overflow-y-auto px-3 py-2">
              <p v-if="!s.sources.length" class="text-xs text-gray-400">
                The agent declares data sources as it plans the metric.
              </p>
              <div
                v-for="src in s.sources"
                :key="src.name"
                class="mb-2 border rounded px-2 py-1.5"
              >
                <div class="flex items-center gap-1.5">
                  <span
                    class="w-1.5 h-1.5 rounded-full flex-shrink-0"
                    :class="{
                      'bg-green-500': src.status === 'ready',
                      'bg-amber-400': src.status === 'building',
                      'bg-gray-300': !src.status || src.status === 'planned',
                    }"
                  />
                  <span class="text-[11px] font-medium text-gray-700 truncate">{{ src.name }}</span>
                  <span v-if="src.role" class="text-[10px] text-gray-400 ml-auto flex-shrink-0">{{ src.role }}</span>
                </div>
                <p v-if="src.description" class="text-[10px] text-gray-500 mt-0.5 leading-tight">{{ src.description }}</p>
              </div>
            </div>
          </div>

          <!-- R code when it is a secondary panel -->
          <div v-if="hasPanel('r-code') && primaryPanel !== 'r-code'" class="flex-1 min-w-0 flex flex-col border rounded-lg bg-white">
            <div class="flex items-center gap-2 px-3 py-2 border-b">
              <span class="text-xs font-semibold text-gray-600 uppercase tracking-wide">R code</span>
              <span v-if="s.rCode" class="text-[10px] text-gray-400 font-mono">{{ s.rCode.name }}</span>
              <span v-if="s.rCode" class="ml-auto text-[10px] text-gray-400">{{ s.rCode.status }}</span>
            </div>
            <div class="flex-1 min-h-0 overflow-auto bg-gray-900">
              <pre v-if="s.rCode" class="p-3 text-[11px] leading-relaxed text-gray-100 font-mono whitespace-pre-wrap break-words">{{ s.rCode.script }}</pre>
              <p v-else class="text-xs text-gray-400 p-4">The analysis agent's current R script appears here as it designs the study.</p>
            </div>
          </div>

          <!-- File (the grid the agent is reading, mirrored) -->
          <div v-if="hasPanel('file')" class="flex-1 min-w-0 flex flex-col border rounded-lg bg-white">
            <div class="flex items-center gap-2 px-3 py-2 border-b">
              <span class="text-xs font-semibold text-gray-600 uppercase tracking-wide">File</span>
              <span
                v-if="s.fileSample"
                class="text-xs text-gray-400 font-mono truncate"
                :title="s.fileSample.key"
              >{{ (s.fileSample.key || '').split('/').pop() }}</span>
              <!-- Upload lands in the tenant's uploads/ area, then the agent is
                   told the key in-conversation — it reads it with its own
                   read_file_sample, which refreshes this panel. -->
              <label
                class="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-primary-500 text-white cursor-pointer whitespace-nowrap"
                :class="{ 'opacity-50 pointer-events-none': fileUploading }"
              >
                {{ fileUploading ? 'Uploading…' : 'Upload file' }}
                <input
                  type="file"
                  class="hidden"
                  accept=".csv,.xlsx,.xls,.xlsm,.xlsb,.zip,.txt"
                  :disabled="fileUploading"
                  @change="uploadFileToSession"
                />
              </label>
            </div>
            <div class="flex-1 min-h-0 overflow-auto px-3 py-2">
              <p v-if="!s.fileSample" class="text-xs text-gray-400">
                Shows which file the agent is reading and its shape — sheets,
                delimiter, row counts. Upload a file to hand it something new.
              </p>
              <template v-else>
                <p class="text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">Last read by agent</p>
                <p class="text-[11px] font-mono text-gray-700 break-all">{{ s.fileSample.key }}</p>
                <p class="text-[10px] text-gray-400 mt-1">{{ s.fileSample.note }}</p>
              </template>
            </div>
          </div>

          <!-- Proposal (the schema draft as the agent shapes it) -->
          <div v-if="hasPanel('proposal')" class="flex-1 min-w-0 flex flex-col border rounded-lg bg-white">
            <div class="flex items-center gap-2 px-3 py-2 border-b">
              <span class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Proposal</span>
              <span
                v-if="s.proposal"
                class="text-[10px] px-1.5 py-0.5 rounded ml-auto"
                :class="{
                  'bg-gray-100 text-gray-500': s.proposal.status === 'proposed',
                  'bg-green-100 text-green-700': s.proposal.status === 'created',
                  'bg-red-100 text-red-700': s.proposal.status === 'rejected',
                }"
              >{{ s.proposal.status === 'created' ? 'draft created' : s.proposal.status }}</span>
            </div>
            <div class="flex-1 min-h-0 overflow-y-auto px-3 py-2">
              <p v-if="!s.proposal" class="text-xs text-gray-400">
                The schema the agent proposes takes shape here — target table,
                columns and, most importantly, the primary key.
              </p>
              <template v-else>
                <p class="text-[11px] font-mono font-medium text-gray-800 break-all">
                  {{ s.proposal.snowflake_schema }}.{{ s.proposal.table_name }}
                </p>
                <p class="text-[10px] mt-1" :class="s.proposal.append_only ? 'text-amber-700' : 'text-gray-600'">
                  <span class="font-semibold uppercase tracking-wide">Key:</span>
                  {{ proposalKeyLabel }}
                </p>
                <p v-if="s.proposal.append_only && s.proposal.append_only_reason" class="text-[10px] text-gray-500 mt-0.5">
                  {{ s.proposal.append_only_reason }}
                </p>
                <p v-if="s.proposal.error" class="text-[10px] text-red-600 mt-1">{{ s.proposal.error }}</p>
                <div class="mt-2 border-t pt-1.5">
                  <p
                    v-for="col in s.proposal.columns"
                    :key="col.name || col"
                    class="text-[10px] font-mono text-gray-600 truncate"
                  >
                    {{ col.name || col }}
                    <span class="text-gray-400">{{ col.snowflake_type || 'VARCHAR' }}</span>
                  </p>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import { reactive } from 'vue';
import axios from 'axios';
import { AgGridVue } from 'ag-grid-vue3';
import PageHeader from '@/components/widgetcomponents/library/PageHeader.vue';
import Chart_V5 from '@/components/widgetcomponents/Viz_Elements/Chart_V5.vue';
import Chart_V6 from '@/components/widgetcomponents/Viz_Elements/Chart_V6.vue';
import Sankey_Diagram from '@/components/widgetcomponents/Viz_Elements/Sankey_Diagram.vue';
import HexMap from '@/components/widgetcomponents/Viz_Elements/HexMap.vue';
import Pie_Chart from '@/components/widgetcomponents/Viz_Elements/Pie_Chart.vue';
import { buildVizProps } from '@/utils/metricInsightAdapter';
import { standardListTheme } from '@/utils/common';
import { drawingDoc } from '@/utils/agentSandbox';

// SESSIONS LIVE AT MODULE SCOPE so switching studio tabs (which unmounts this
// component) does not kill the conversation view. The TRANSPORT is polling
// (2026-08-31, websockets removed): the conversation is rows in the backend's
// AgentSessionEvent table; we POST turns and poll new events by id cursor.
// The worker is not attached to this page at all — a reload, a tab close or
// a worker idle-exit no longer ends the session; the next message relaunches
// a worker that resumes from the stored stream.
//
// Keyed by INSTANCE (mode + metric + file), not a single global: the agent is
// one reusable runner with many instances, and each mount owns its own
// session. Before this was a singleton, the ingestion agent and the Metric
// Wizard silently shared one live session.
const SESSIONS = new Map();

function sessionEntry(key) {
  if (!SESSIONS.has(key)) {
    SESSIONS.set(key, {
      key,
      state: reactive({
        connState: 'idle',   // idle | connecting | launching | ready | thinking | closed
        closeReason: '',
        blocks: [],
        visuals: [],
        sources: [],
        fileSample: null,
        proposal: null,
        rCode: null,
        rOutput: null,
        totalCost: 0,
        thinkingSince: null,
        retryInSeconds: null,
        extraSystemPrompt: '',
      }),
      sessionId: null,
      cursor: 0,            // highest event row id applied
      ownSeqs: new Set(),   // rows this tab POSTed itself (already rendered)
      pollToken: 0,
      pollActive: false,
      posting: 0,           // in-flight turn POSTs — polls hold until settled
      lastEventAt: 0,
      view: null,   // the mounted component, for autoscroll only
    });
  }
  return SESSIONS.get(key);
}

function turnUrl(entry) {
  return `/api/impact_analytics/agent-sessions/${entry.sessionId}/turn/`;
}

function applyEnvelope(entry, data) {
  const s = entry.state;
  const map = {
    idle: 'ready', thinking: 'thinking', launching: 'launching',
    rate_limited: 'rate_limited', closed: 'closed',
  };
  const st = map[data.state] || 'ready';
  if (st === 'thinking' && s.connState !== 'thinking') s.thinkingSince = Date.now();
  if (st !== 'thinking') s.thinkingSince = null;
  s.connState = st;
  s.retryInSeconds = data.retry_in_seconds ?? null;
  if (typeof data.total_cost_usd === 'number') s.totalCost = data.total_cost_usd;
  if (st === 'closed') {
    s.closeReason = data.status === 'died'
      ? 'The agent process died — see the session logs.' : 'Session ended.';
  }
}

function hasPendingQuestion(entry) {
  return entry.state.blocks.some((b) => b.kind === 'question' && !b.answered);
}

const sleep = (ms) => new Promise((r) => { setTimeout(r, ms); });

async function pollOnce(entry) {
  const { data } = await axios.get(
    `/api/impact_analytics/agent-sessions/${entry.sessionId}/stream/`,
    { params: { after: entry.cursor } },
  );
  let fresh = 0;
  for (const ev of data.events || []) {
    if (ev.seq > entry.cursor) entry.cursor = ev.seq;
    if (entry.ownSeqs.has(ev.seq)) continue;
    handleEvent(entry, ev);
    fresh += 1;
  }
  applyEnvelope(entry, data);
  return fresh;
}

// THE CADENCE — the point of the polled transport. Fast (300ms) while the
// agent's output is actually streaming; settling to 1s then 2.5s when a turn
// runs long with nothing new (wait-tool job polling, long queries); a couple
// of confirming polls after the turn ends; then NOTHING AT ALL while the
// agent is waiting on the user — an idle conversation costs zero requests,
// which is what makes the fast polling affordable. Sending a message, an
// answer or an interrupt wakes the loop; so does the tab becoming visible.
function wakePolling(entry) {
  if (entry.pollActive || !entry.sessionId) return;
  const token = ++entry.pollToken;
  entry.pollActive = true;
  entry.lastEventAt = Date.now();
  (async () => {
    let errorDelay = 1000;
    let readyPolls = 0;
    try {
      while (entry.pollToken === token) {
        if (document.hidden) { await sleep(1000); continue; }
        if (entry.posting > 0) { await sleep(100); continue; }
        let fresh = 0;
        try {
          fresh = await pollOnce(entry);
          errorDelay = 1000;
        } catch (err) {
          const code = err && err.response && err.response.status;
          if (code === 404 || code === 401 || code === 403) {
            entry.state.connState = 'closed';
            entry.state.closeReason = 'Session unavailable.';
            return;
          }
          await sleep(errorDelay);
          errorDelay = Math.min(errorDelay * 2, 30000);
          continue;
        }
        if (fresh) entry.lastEventAt = Date.now();
        const st = entry.state.connState;
        if (st === 'closed') return;
        if (st === 'ready' || hasPendingQuestion(entry)) {
          // The agent is waiting on the user. A couple of confirming polls
          // (a follow-up event can trail the ready flip), then stop cold.
          readyPolls += 1;
          if (readyPolls >= 3 && !fresh) return;
          await sleep(1000);
          continue;
        }
        readyPolls = 0;
        if (st === 'launching') { await sleep(2000); continue; }
        if (st === 'rate_limited') { await sleep(1500); continue; }
        const quiet = Date.now() - entry.lastEventAt;
        if (quiet < 6000) await sleep(300);
        else if (quiet < 30000) await sleep(1000);
        else await sleep(2500);
      }
    } finally {
      if (entry.pollToken === token) entry.pollActive = false;
    }
  })();
}

function stopPolling(entry) {
  entry.pollToken += 1;
  entry.pollActive = false;
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      SESSIONS.forEach((entry) => {
        const st = entry.state.connState;
        if (entry.sessionId && st !== 'closed' && st !== 'idle') wakePolling(entry);
      });
    }
  });
}

function handleEvent(entry, ev) {
  const s = entry.state;
  if (ev.type === 'ping') return;
  if (ev.type === 'status') {
    if (ev.state === 'launching' || ev.state === 'starting') s.connState = 'launching';
    else if (ev.state === 'rate_limited') {
      s.connState = 'rate_limited';
      s.retryInSeconds = ev.retry_in_seconds;
    }
    else if (ev.state === 'ready') {
      s.connState = 'ready';
      s.thinkingSince = null;
    } else if (ev.state === 'thinking') {
      s.connState = 'thinking';
      s.thinkingSince = Date.now();
    }
    return;
  }
  if (ev.type === 'user') {
    // Only present in STORED streams — live user turns are pushed locally by
    // sendDraft. Lets replay run through this same pipeline.
    pushBlock(entry, { kind: 'user', text: ev.text });
  } else if (ev.type === 'text') {
    pushBlock(entry, { kind: 'text', text: ev.text });
  } else if (ev.type === 'text_delta') {
    let block = [...s.blocks].reverse().find((b) => b.kind === 'text' && b.id === ev.id);
    if (!block) {
      block = { kind: 'text', id: ev.id, text: '' };
      pushBlock(entry, block);
    }
    block.text += ev.text;
    if (entry.view) entry.view.scrollChat();
  } else if (ev.type === 'text_end') {
    // The streamed text block is already rendered; end only marks its protocol boundary.
  } else if (ev.type === 'ask_user') {
    pushBlock(entry, { kind: 'question', callId: ev.call_id, question: ev.question,
      options: ev.options || [], responseText: '', answered: '' });
  } else if (ev.type === 'user_answer') {
    const block = [...s.blocks].reverse().find((b) => b.kind === 'question' && b.callId === ev.call_id);
    if (block) block.answered = ev.label || ev.text || 'Written response';
  } else if (ev.type === 'thinking_start') {
    pushBlock(entry, { kind: 'thinking', id: ev.id, text: '', done: false, collapsed: false });
  } else if (ev.type === 'thinking_delta') {
    let block = [...s.blocks].reverse().find((b) => b.kind === 'thinking' && b.id === ev.id);
    if (!block) {
      block = { kind: 'thinking', id: ev.id, text: '', done: false, collapsed: false };
      pushBlock(entry, block);
    }
    block.text += ev.text;
    if (entry.view) entry.view.scrollChat();
  } else if (ev.type === 'thinking_end') {
    const block = [...s.blocks].reverse().find((b) => b.kind === 'thinking' && b.id === ev.id);
    if (block) {
      block.done = true;
      block.collapsed = true;
    }
  } else if (ev.type === 'drawing') {
    upsertVisual(entry, { id: ev.id, title: ev.title || 'Untitled', viz_type: 'drawing', svg: ev.svg });
  } else if (ev.type === 'visual') {
    // The insight pipeline renders the scratchpad: same adapter, same
    // components — a visual here IS a saveable metric-insight config.
    let viz = null;
    try {
      viz = buildVizProps(ev.viz_type, ev.viz_config, ev.rows);
    } catch {
      viz = null;
    }
    upsertVisual(entry, { id: ev.id, title: ev.title || 'Untitled', viz_type: ev.viz_type, viz });
  } else if (ev.type === 'scratchpad_clear') {
    if (ev.ids && ev.ids.length) {
      s.visuals = s.visuals.filter((v) => !ev.ids.includes(v.id));
    } else {
      s.visuals = [];
    }
  } else if (ev.type === 'sources_update') {
    s.sources = ev.sources || [];
  } else if (ev.type === 'r_code') {
    s.rCode = {
      script: ev.script || '',
      name: ev.name || 'analysis.R',
      status: ev.status || 'draft',
    };
  } else if (ev.type === 'r_output') {
    s.rOutput = ev;
    if (entry.view) {
      entry.view.rPane = 'output';
      entry.view.rOutputIndex = 0;
      entry.view.fetchROutputPlots();
    }
  } else if (ev.type === 'file_sample') {
    // The grid the agent is reading — mirrored so the human confirms
    // "row 7 is the header" by looking at the same frame.
    s.fileSample = ev;
  } else if (ev.type === 'schema_proposal') {
    s.proposal = ev;
  } else if (ev.type === 'create_result') {
    const labels = {
      metric: 'Metric', flag: 'Patient flag', worklist: 'Worklist', insight: 'Insight',
      data_attribute: 'DataAttribute', update_data_attribute: 'DataAttribute',
      study: 'R study', update_study: 'R study', run_study: 'R analysis', dataset: 'Dataset',
      data_parser_schema: 'Parser schema', update_data_parser_schema: 'Parser schema',
      enable_schema: 'Parser schema', routine: 'Routine', update_routine: 'Routine',
      run_parse: 'Parse run', parse_file: 'File load',
    };
    pushBlock(entry, {
      kind: 'created',
      ok: ev.ok,
      artifactLabel: labels[ev.artifact_kind] || 'Artifact',
      action: ev.action || 'created',
      id: ev.id,
      name: ev.name,
      error: ev.error,
    });
  } else if (ev.type === 'turn_done') {
    if (ev.cost_usd) s.totalCost += ev.cost_usd;
    pushBlock(entry, {
      kind: 'meta',
      text: `— ${ev.turns ?? '?'} turns${ev.cost_usd ? ` · $${ev.cost_usd.toFixed(2)}` : ''}`,
    });
  } else if (ev.type === 'error' || ev.type === 'fatal') {
    pushBlock(entry, { kind: 'error', text: ev.message });
  }
}

function pushBlock(entry, block) {
  entry.state.blocks.push(block);
  if (entry.view) entry.view.scrollChat();
}

// The agent curates the pad: same id replaces the card in place (iteration),
// a new id appends. Only fresh cards get a conversation chip — updates don't
// re-announce.
function upsertVisual(entry, v) {
  const i = entry.state.visuals.findIndex((x) => x.id === v.id);
  if (i >= 0) {
    entry.state.visuals.splice(i, 1, v);
  } else {
    entry.state.visuals.push(v);
    pushBlock(entry, { kind: 'visual_ref', title: v.title, visualId: v.id });
  }
  if (entry.view) entry.view.padPage = entry.state.visuals.length - 1;
}

export default {
  name: 'AgentExplore',
  components: { PageHeader, AgGridVue, Chart_V5, Chart_V6, Sankey_Diagram, HexMap, Pie_Chart },
  props: {
    embedded: { type: Boolean, default: false },
    autoStart: { type: Boolean, default: false },
    extraSystemPrompt: { type: String, default: '' },
    metricId: { type: [Number, String], default: null },
    studyId: { type: [Number, String], default: null },
    startLabel: { type: String, default: '' },
    // Which instance of the agent to run: metric, ingestion, or analysis.
    sessionMode: { type: String, default: 'metric' },
    s3Key: { type: String, default: '' },
    schemaId: { type: [Number, String], default: null },
    // Which reference widgets this mount wants beside the conversation.
    // All optional; the default is the Metric Studio trio. Known panels:
    // scratchpad | r-code | selected-data | tables | sources | file | proposal.
    panels: { type: Array, default: () => ['scratchpad', 'tables', 'sources'] },
    selectedInputs: { type: Array, default: () => [] },
  },
  emits: ['select-data'],
  data() {
    const entry = sessionEntry(
      `${this.sessionMode}:${this.metricId || ''}:${this.studyId || ''}:${this.s3Key || ''}`,
    );
    const padStorageKey = this.sessionMode === 'analysis'
      ? 'agent_pad_pct_analysis' : 'agent_pad_pct';
    const defaultPadPct = this.sessionMode === 'analysis' ? 64 : 60;
    return {
      entry,
      s: entry.state,
      draft: '',
      now: Date.now(),
      ticker: null,
      defaultColDef: { sortable: true, resizable: true, filter: true },
      listTheme: standardListTheme,
      showHistory: false,
      pastSessions: [],
      fileUploading: false,
      resumeId: null,   // past session_id the next connect() continues from
      erdSchemas: [],
      padPct: Number(localStorage.getItem(padStorageKey)) || defaultPadPct,
      padPage: 0,
      rPane: 'code',
      rOutputIndex: 0,
      rOutputPlotUrls: {},
      dragging: false,
    };
  },
  computed: {
    tenantSlug() {
      return this.$store.state.tenant.current_tenant_slug;
    },
    padStorageKey() {
      return this.sessionMode === 'analysis'
        ? 'agent_pad_pct_analysis' : 'agent_pad_pct';
    },
    defaultPadPct() {
      return this.sessionMode === 'analysis' ? 64 : 60;
    },
    storageKey() {
      return `agent-session:${this.sessionMode}:${this.metricId || ''}:${this.studyId || ''}:${this.s3Key || ''}`;
    },
    sessionActive() {
      return ['connecting', 'launching', 'ready', 'thinking', 'rate_limited'].includes(this.s.connState);
    },
    thinking() {
      return this.s.connState === 'thinking';
    },
    hasPendingQuestion() {
      return this.s.blocks.some((b) => b.kind === 'question' && !b.answered);
    },
    contextMismatch() {
      return this.sessionActive && !!this.extraSystemPrompt
        && this.s.extraSystemPrompt !== this.extraSystemPrompt;
    },
    thinkingElapsed() {
      if (!this.s.thinkingSince) return '';
      const secs = Math.max(0, Math.floor((this.now - this.s.thinkingSince) / 1000));
      return secs < 60 ? `${secs}s` : `${Math.floor(secs / 60)}m ${secs % 60}s`;
    },
    stateLabel() {
      const st = this.s.connState;
      if (st === 'idle') return 'no session';
      if (st === 'connecting') return 'connecting…';
      if (st === 'launching') return 'starting the agent — it introduces itself when the context is loaded (~1-2 min)…';
      if (st === 'ready') return 'agent ready';
      if (st === 'thinking') return `agent working — ${this.thinkingElapsed}`;
      if (st === 'rate_limited') return `Vertex rate-limited — retrying in ${this.s.retryInSeconds || '?'}s…`;
      return this.s.closeReason || 'disconnected';
    },
    dotClass() {
      const st = this.s.connState;
      if (st === 'ready') return 'bg-green-500';
      if (st === 'thinking') return 'bg-orange-400';
      if (st === 'rate_limited') return 'bg-amber-400';
      if (st === 'closed' || st === 'idle') return 'bg-gray-300';
      return 'bg-yellow-400';
    },
    currentVisual() {
      if (!this.s.visuals.length) return null;
      const idx = Math.min(this.padPage, this.s.visuals.length - 1);
      return this.s.visuals[idx];
    },
    erdTableCount() {
      return this.erdSchemas.reduce((n, s) => n + Object.keys(s.tables).length, 0);
    },
    rOutputs() {
      return (this.s.rOutput && this.s.rOutput.results
        && this.s.rOutput.results.outputs) || [];
    },
    currentROutput() {
      return this.rOutputs[this.rOutputIndex] || null;
    },
    rOutputColumnDefs() {
      if (!this.currentROutput || this.currentROutput.kind !== 'table') return [];
      return (this.currentROutput.columns || []).map((column) => ({
        field: column,
        headerName: column,
      }));
    },
    rOutputDefaultColDef() {
      return {
        ...this.defaultColDef,
        cellClass: 'sensitive',
        wrapText: true,
        autoHeight: true,
        minWidth: 120,
        flex: 1,
      };
    },
    selectedDatasets() {
      return this.selectedInputs.filter((item) => item.source_type === 'dataset');
    },
    selectedAttributes() {
      return this.selectedInputs.filter((item) => item.source_type === 'attribute');
    },
    bottomPanels() {
      return this.panels.filter((p) => p !== this.primaryPanel && p !== 'selected-data');
    },
    primaryPanel() {
      if (this.panels.includes('r-code')) return 'r-code';
      if (this.panels.includes('scratchpad')) return 'scratchpad';
      return null;
    },
    proposalKeyLabel() {
      const p = this.s.proposal;
      if (!p) return '';
      if (p.append_only) return 'APPEND-ONLY';
      return (p.primary_key_columns || []).join(', ') || 'no key';
    },
    composerPlaceholder() {
      if (this.s.connState === 'closed') return this.stateLabel;
      return 'Ask about the data, or state an assumption to test… (Enter to send)';
    },
  },
  async mounted() {
    this.entry.view = this;
    this.ticker = setInterval(() => { this.now = Date.now(); }, 1000);
    this.scrollChat();
    if (this.s.rOutput) this.fetchROutputPlots();
    if (this.hasPanel('tables')) this.fetchErd();
    // The session lives server-side now, so a reload doesn't end it — try to
    // REATTACH to the live session this mount key was last driving before
    // considering an auto-start.
    if (!this.sessionActive) {
      const reattached = await this.reattach();
      if (!reattached && this.autoStart) this.connect();
    }
  },
  beforeUnmount() {
    // Deliberately does NOT end the session — the conversation lives in the
    // backend and survives tab switches, reloads and worker exits alike. End
    // it with the End session button.
    if (this.entry.view === this) this.entry.view = null;
    clearInterval(this.ticker);
    this.clearROutputPlots();
  },
  methods: {
    hasPanel(name) {
      return this.panels.includes(name);
    },
    rOutputPlotKey(output) {
      return (output && output.file) || '';
    },
    clearROutputPlots() {
      Object.values(this.rOutputPlotUrls).forEach((url) => URL.revokeObjectURL(url));
      this.rOutputPlotUrls = {};
    },
    async fetchROutputPlots() {
      if (!this.s.rOutput || this.s.rOutput.status !== 'completed') return;
      this.clearROutputPlots();
      const urls = {};
      for (const output of this.rOutputs.filter((item) => item.kind === 'plot')) {
        const file = this.rOutputPlotKey(output);
        const name = file.replace(/^plots\//, '').replace(/\.png$/, '');
        if (!name) continue;
        try {
          const response = await axios.get(
            `/api/impact_analytics/study-runs/${this.s.rOutput.run_id}/artifact/`,
            { params: { name }, responseType: 'blob' },
          );
          urls[file] = URL.createObjectURL(response.data);
        } catch {
          urls[file] = '';
        }
      }
      this.rOutputPlotUrls = urls;
    },
    resetConversation() {
      const s = this.s;
      s.closeReason = '';
      s.extraSystemPrompt = this.extraSystemPrompt;
      s.blocks = [];
      s.visuals = [];
      s.sources = [];
      s.fileSample = null;
      s.proposal = null;
      s.rOutput = null;
      this.rPane = 'code';
      this.rOutputIndex = 0;
      this.clearROutputPlots();
      s.totalCost = 0;
      this.entry.cursor = 0;
      this.entry.ownSeqs = new Set();
    },
    async connect() {
      const s = this.s;
      s.connState = 'connecting';
      this.resetConversation();
      try {
        const { data } = await axios.post('/api/impact_analytics/agent-sessions/start/', {
          session_mode: this.sessionMode,
          extra_system_prompt: this.extraSystemPrompt || '',
          metric_id: this.metricId || null,
          study_id: this.studyId || null,
          // The backend re-validates this against the tenant's own S3 prefix
          // — never trust the browser with a key into a shared bucket.
          s3_key: this.s3Key || '',
          schema_id: this.schemaId || null,
          resume_session_id: this.resumeId || '',
        });
        this.entry.sessionId = data.session_id;
        this.entry.cursor = data.cursor || 0;
        try { sessionStorage.setItem(this.storageKey, data.session_id); } catch { /* fine */ }
        s.connState = 'launching';
        wakePolling(this.entry);
      } catch (err) {
        s.connState = 'closed';
        s.closeReason = (err && err.response && err.response.data
          && err.response.data.error) || 'Could not start the session.';
      }
    },
    async reattach() {
      // A live session survives reloads — pick it back up from the stored
      // stream (every row carries its seq, so the cursor lands correctly).
      let stored = null;
      try { stored = sessionStorage.getItem(this.storageKey); } catch { /* fine */ }
      if (!stored) return false;
      try {
        const { data } = await axios.get(
          `/api/impact_analytics/agent-sessions/${stored}/stream/`,
          { params: { after: 0 } },
        );
        if (data.status !== 'active') {
          sessionStorage.removeItem(this.storageKey);
          return false;
        }
        this.resetConversation();
        this.entry.sessionId = stored;
        for (const ev of data.events || []) {
          if (ev.seq > this.entry.cursor) this.entry.cursor = ev.seq;
          handleEvent(this.entry, ev);
        }
        applyEnvelope(this.entry, data);
        // A question the (now dead) worker asked can still be answered — the
        // answer relaunches a worker that resumes with it folded in. Only
        // when the worker is gone does it read as stale, so leave live ones.
        wakePolling(this.entry);
        return true;
      } catch {
        return false;
      }
    },
    endSession() {
      const entry = this.entry;
      stopPolling(entry);
      try { sessionStorage.removeItem(this.storageKey); } catch { /* fine */ }
      if (entry.sessionId) {
        axios.post(turnUrl(entry), { type: 'end' }).catch(() => {});
      }
      this.s.connState = 'closed';
      this.s.thinkingSince = null;
      this.s.closeReason = 'Session ended.';
    },
    startLive() {
      this.showHistory = false;
      this.resumeId = null;
      this.connect();
    },
    async resumeSession(sessionId) {
      // Live continuation: the relaunched worker boots holding the old
      // session's conversation as real history; the UI preloads it so the
      // thread reads unbroken, then live events append.
      const { data } = await axios.get(`/api/impact_analytics/agent-sessions/${sessionId}/`);
      this.showHistory = false;
      this.resumeId = sessionId;
      await this.connect();
      (data.events || []).forEach((ev) => handleEvent(this.entry, ev));
      // A question left unanswered in the old session must not lock the
      // composer of the new one.
      this.s.blocks.forEach((b) => {
        if (b.kind === 'question' && !b.answered) b.answered = '(expired)';
      });
      pushBlock(this.entry, { kind: 'meta', text: '— resumed from a previous session —' });
    },
    startDivider() {
      this.dragging = true;
      const move = (e) => {
        const box = this.$refs.panes && this.$refs.panes.getBoundingClientRect();
        if (!box || !box.width) return;
        const pct = ((box.right - e.clientX) / box.width) * 100;
        this.padPct = Math.min(70, Math.max(25, Math.round(pct * 10) / 10));
      };
      const up = () => {
        this.dragging = false;
        localStorage.setItem(this.padStorageKey, String(this.padPct));
        window.removeEventListener('mousemove', move);
        window.removeEventListener('mouseup', up);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
      window.addEventListener('mousemove', move);
      window.addEventListener('mouseup', up);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    resetDivider() {
      this.padPct = this.defaultPadPct;
      localStorage.setItem(this.padStorageKey, String(this.defaultPadPct));
    },
    async toggleHistory() {
      this.showHistory = !this.showHistory;
      if (this.showHistory) {
        const { data } = await axios.get(
          '/api/impact_analytics/agent-sessions/',
          { params: { session_mode: this.sessionMode } },
        );
        this.pastSessions = Array.isArray(data) ? data : (data.results || []);
      }
    },
    scrollChat() {
      this.$nextTick(() => {
        const el = this.$refs.scroll;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
    scrollPad() {
      this.$nextTick(() => {
        const el = this.$refs.padScroll;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
    drawingDoc,
    drawingAspect(v) {
      const m = /viewBox\s*=\s*["']\s*[\d.-]+\s+[\d.-]+\s+([\d.]+)\s+([\d.]+)/.exec(v.svg || '');
      if (m && Number(m[2]) > 0) return `${m[1]} / ${m[2]}`;
      return '2 / 1';
    },
    scrollToVisual(id) {
      const idx = this.s.visuals.findIndex((v) => v.id === id);
      if (idx >= 0) this.padPage = idx;
    },
    sendUserText(text) {
      const entry = this.entry;
      if (!text || !entry.sessionId || !this.sessionActive) return false;
      pushBlock(entry, { kind: 'user', text });
      // Optimistic: the envelope confirms (or corrects) on the next poll.
      this.s.connState = 'thinking';
      this.s.thinkingSince = Date.now();
      entry.posting += 1;
      axios.post(turnUrl(entry), { type: 'user', text })
        .then(({ data }) => { if (data.seq) entry.ownSeqs.add(data.seq); })
        .catch((err) => {
          pushBlock(entry, {
            kind: 'error',
            text: (err && err.response && err.response.data
              && err.response.data.error) || 'Message failed to send.',
          });
        })
        .finally(() => { entry.posting -= 1; wakePolling(entry); });
      return true;
    },
    sendDraft() {
      if (this.sendUserText(this.draft.trim())) this.draft = '';
    },
    async uploadFileToSession(event) {
      const file = event.target.files[0];
      event.target.value = '';
      if (!file) return;
      this.fileUploading = true;
      try {
        const { data: presigned } = await axios.post('/api/etl_module/upload/', {
          filename: file.name,
          content_type: file.type || 'application/octet-stream',
        });
        // Plain fetch: the default axios interceptor appends tenant_slug to
        // every request, which corrupts a presigned S3 URL's signature.
        const put = await fetch(presigned.upload_url, {
          method: 'PUT',
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
          body: file,
        });
        if (!put.ok) {
          throw new Error(`S3 upload failed (${put.status})`);
        }
        // Indicator only — the user decides what to tell the agent.
        pushBlock(this.entry, {
          kind: 'upload', ok: true, name: file.name, key: presigned.s3_key,
        });
      } catch (err) {
        pushBlock(this.entry, {
          kind: 'upload', ok: false, name: file.name,
          error: (err && err.message) || 'Upload failed.',
        });
      }
      this.fileUploading = false;
    },
    answerQuestion(block, label = '') {
      const entry = this.entry;
      const text = (block.responseText || '').trim();
      if (block.answered || (!label && !text) || !entry.sessionId) return;
      block.answered = label || text;
      entry.posting += 1;
      axios.post(turnUrl(entry), {
        type: 'user_answer', call_id: block.callId, label, text,
      })
        .then(({ data }) => { if (data.seq) entry.ownSeqs.add(data.seq); })
        .catch(() => {})
        .finally(() => { entry.posting -= 1; wakePolling(entry); });
    },
    interrupt() {
      const entry = this.entry;
      if (!entry.sessionId) return;
      axios.post(turnUrl(entry), { type: 'interrupt' })
        .then(({ data }) => { if (data.seq) entry.ownSeqs.add(data.seq); })
        .catch(() => {})
        .finally(() => wakePolling(entry));
    },
    async fetchErd() {
      if (this.erdSchemas.length) return;
      try {
        const { data } = await axios.get('/api/impact_analytics/warehouse-catalog/');
        const schemas = (data.schemas || [])
          .map((s) => {
            const tables = {};
            for (const [name, t] of Object.entries(s.tables || {})) {
              if (t.use_for_metrics) tables[name] = t;
            }
            if (!Object.keys(tables).length) return null;
            return { name: s.name, tables, _open: false };
          })
          .filter(Boolean);
        this.erdSchemas = schemas;
      } catch {
        // catalog unavailable — panel stays empty
      }
    },
  },
};
</script>

<style scoped>
.ae-divider {
  width: 12px;
  margin: 0 -2px;
  flex-shrink: 0;
  cursor: col-resize;
  position: relative;
}
.ae-divider::after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: 5px;
  width: 2px;
  border-radius: 2px;
  background: #e5e4df;
  transition: background 0.15s;
}
.ae-divider:hover::after {
  background: #f59e0b;
}
</style>
